import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { mandantenApi } from '../services/api';
import type { Mandant, CreateMandantDto, UpdateMandantDto } from '../types/api';

const Mandanten: React.FC = () => {
  const [mandanten, setMandanten] = useState<Mandant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMandant, setEditingMandant] = useState<Mandant | null>(null);
  const [formData, setFormData] = useState<CreateMandantDto>({
    mandantName: '',
    mandantSchluessel: '',
    status: 'aktiv',
  });

  useEffect(() => {
    loadMandanten();
  }, []);

  const loadMandanten = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mandantenApi.getAll();
      setMandanten(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Mandanten:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Mandanten');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mandant?: Mandant) => {
    if (mandant) {
      setEditingMandant(mandant);
      setFormData({
        mandantName: mandant.mandantName,
        mandantSchluessel: mandant.mandantSchluessel,
        status: mandant.status,
        maxBenutzer: mandant.maxBenutzer,
        ablaufdatum: mandant.ablaufdatum,
      });
    } else {
      setEditingMandant(null);
      setFormData({
        mandantName: '',
        mandantSchluessel: '',
        status: 'aktiv',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMandant(null);
    setFormData({
      mandantName: '',
      mandantSchluessel: '',
      status: 'aktiv',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingMandant) {
        await mandantenApi.update(editingMandant.mandantID, formData as UpdateMandantDto);
      } else {
        await mandantenApi.create(formData);
      }
      handleCloseDialog();
      loadMandanten();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Möchten Sie diesen Mandanten wirklich löschen?')) {
      return;
    }

    try {
      await mandantenApi.delete(id);
      loadMandanten();
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      setError(err.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aktiv':
        return 'success';
      case 'inaktiv':
        return 'default';
      case 'test':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Mandanten-Verwaltung</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Neuer Mandant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Schlüssel</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Max. Benutzer</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mandanten.map((mandant) => (
              <TableRow key={mandant.mandantID}>
                <TableCell>{mandant.mandantID}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    {mandant.mandantName}
                  </Box>
                </TableCell>
                <TableCell>
                  <code>{mandant.mandantSchluessel}</code>
                </TableCell>
                <TableCell>
                  <Chip
                    label={mandant.status}
                    color={getStatusColor(mandant.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{mandant.maxBenutzer || '∞'}</TableCell>
                <TableCell>
                  {new Date(mandant.erstelltAm).toLocaleDateString('de-DE')}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(mandant)}
                    title="Bearbeiten"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(mandant.mandantID)}
                    disabled={mandant.mandantID === 1}
                    title={
                      mandant.mandantID === 1
                        ? 'Standard-Mandant kann nicht gelöscht werden'
                        : 'Löschen'
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog für Erstellen/Bearbeiten */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMandant ? 'Mandant bearbeiten' : 'Neuer Mandant'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Mandantenname"
              value={formData.mandantName}
              onChange={(e) =>
                setFormData({ ...formData, mandantName: e.target.value })
              }
              required
              fullWidth
            />
            <TextField
              label="Mandantenschlüssel"
              value={formData.mandantSchluessel}
              onChange={(e) =>
                setFormData({ ...formData, mandantSchluessel: e.target.value })
              }
              required
              fullWidth
              disabled={!!editingMandant}
              helperText={
                editingMandant
                  ? 'Schlüssel kann nicht geändert werden'
                  : 'Eindeutiger Schlüssel (z.B. firma-xyz)'
              }
            />
            <TextField
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              select
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="aktiv">Aktiv</option>
              <option value="inaktiv">Inaktiv</option>
              <option value="test">Test</option>
            </TextField>
            <TextField
              label="Max. Benutzer"
              type="number"
              value={formData.maxBenutzer || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxBenutzer: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              fullWidth
              helperText="Leer lassen für unbegrenzt"
            />
            <TextField
              label="Ablaufdatum"
              type="date"
              value={
                formData.ablaufdatum
                  ? formData.ablaufdatum.split('T')[0]
                  : ''
              }
              onChange={(e) =>
                setFormData({ ...formData, ablaufdatum: e.target.value || undefined })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.mandantName || !formData.mandantSchluessel}
          >
            {editingMandant ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Mandanten;
